import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, IconButton, Fade } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import ShareIcon from '@mui/icons-material/Share';

const InstallPrompt = () => {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [isMiniPrompt, setIsMiniPrompt] = useState(true); // Start with mini prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      localStorage.setItem('worldexplore-pwa-installed', 'true');
      return;
    }

    // Check if user previously dismissed or installed
    const hasInstalled = localStorage.getItem('worldexplore-pwa-installed') === 'true';
    const hasDismissed = localStorage.getItem('worldexplore-pwa-dismissed');
    const dismissCount = parseInt(localStorage.getItem('worldexplore-pwa-dismiss-count') || '0');
    const dismissedDate = hasDismissed ? new Date(parseInt(hasDismissed)) : null;
    
    // Progressive re-show intervals: 7 days, 14 days, 30 days
    const showAgainAfter = dismissCount === 0 ? 7 * 24 * 60 * 60 * 1000 : 
                           dismissCount === 1 ? 14 * 24 * 60 * 60 * 1000 :
                           30 * 24 * 60 * 60 * 1000;
    
    if (hasInstalled) {
      return;
    }
    
    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    // Listen for custom event to force show the prompt
    const handleForceShow = () => {
      setIsMiniPrompt(false);
      setIsVisible(true);
    };
    
    window.addEventListener('show-install-prompt', handleForceShow);
    
    // Track user engagement (scroll, clicks, time spent)
    let engagementScore = 0;
    let timeOnSite = 0;
    const startTime = Date.now();
    
    const trackEngagement = () => {
      timeOnSite = Math.floor((Date.now() - startTime) / 1000);
      engagementScore = timeOnSite; // Base score on time
    };
    
    const handleScroll = () => {
      engagementScore += 1;
      trackEngagement();
    };
    
    const handleClick = () => {
      engagementScore += 2;
      trackEngagement();
    };
    
    // Add engagement listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('click', handleClick);
    const engagementInterval = setInterval(trackEngagement, 1000);
    
    // Check dismissal date
    if (dismissedDate && (new Date().getTime() - dismissedDate.getTime() < showAgainAfter)) {
      // Still respect dismissal unless forced
      return () => {
        window.removeEventListener('show-install-prompt', handleForceShow);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('click', handleClick);
        clearInterval(engagementInterval);
      };
    }

    // Show mini prompt after engagement threshold (30 seconds OR 5 interactions)
    const showPromptTimer = setTimeout(() => {
      if (engagementScore >= 5 || timeOnSite >= 30) {
        setIsMiniPrompt(true);
        setIsVisible(true);
      }
    }, 30000); // Check after 30 seconds

    // For non-iOS, listen for beforeinstallprompt
    if (!isIOSDevice) {
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setInstallPrompt(e);
      };
      
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('show-install-prompt', handleForceShow);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('click', handleClick);
        clearInterval(engagementInterval);
        clearTimeout(showPromptTimer);
      };
    }
    
    // Check if already in standalone mode
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsVisible(false);
      localStorage.setItem('worldexplore-pwa-installed', 'true');
    };
    
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('show-install-prompt', handleForceShow);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      clearInterval(engagementInterval);
      clearTimeout(showPromptTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!isIOS && installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      setInstallPrompt(null);
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem('worldexplore-pwa-installed', 'true');
        setIsVisible(false);
      }
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    const dismissCount = parseInt(localStorage.getItem('worldexplore-pwa-dismiss-count') || '0');
    localStorage.setItem('worldexplore-pwa-dismiss-count', (dismissCount + 1).toString());
    localStorage.setItem('worldexplore-pwa-dismissed', Date.now().toString());
  };
  
  const handleExpand = () => {
    setIsMiniPrompt(false);
  };

  if (isInstalled || !isVisible) {
    return null;
  }
  
  // Mini prompt (less intrusive)
  if (isMiniPrompt) {
    return (
      <Fade in={isVisible}>
        <Paper 
          elevation={4}
          onClick={handleExpand}
          sx={{
            position: 'fixed',
            bottom: '80px',
            right: '20px',
            width: { xs: '56px', sm: '64px' },
            height: { xs: '56px', sm: '64px' },
            zIndex: 9998,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
            },
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
              '50%': { boxShadow: '0 4px 20px rgba(25, 118, 210, 0.6)' },
            }
          }}
        >
          <AddToHomeScreenIcon sx={{ color: 'white', fontSize: { xs: 28, sm: 32 } }} />
          <IconButton 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            sx={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              backgroundColor: 'error.main',
              color: 'white',
              width: '20px',
              height: '20px',
              '&:hover': {
                backgroundColor: 'error.dark',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Paper>
      </Fade>
    );
  }

  return (
    <Fade in={isVisible}>
      <Paper 
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '90%', sm: '400px' },
          zIndex: 9999,
          p: 2,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.main',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box display="flex" alignItems="center">
            <AddToHomeScreenIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {t('installPrompt.title')}
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleDismiss} 
            aria-label={t('buttons.close')}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {isIOS ? (
          <Box>
            <Typography variant="body2" mb={2} color="text.secondary">
              {t('installPrompt.ios.description')}
            </Typography>
            <Box 
              sx={{ 
                mb: 2, 
                p: 2, 
                backgroundColor: 'grey.100', 
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'primary.main'
              }}
            >
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                component="div" 
                sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: 'primary.main' }}
              >
                <Box 
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%', 
                    backgroundColor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 1,
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  1
                </Box>
                <span>{t('installPrompt.ios.step1')}</span> 
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 0.5,
                    p: 0.5,
                    backgroundColor: 'primary.main',
                    borderRadius: 0.5,
                    color: 'white'
                  }}
                >
                  <ShareIcon fontSize="small" />
                </Box>
                <span>{t('installPrompt.ios.step1Additional')}</span>
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                component="div" 
                sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}
              >
                <Box 
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%', 
                    backgroundColor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 1,
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  2
                </Box>
                <span>{t('installPrompt.ios.step2')}</span> 
                <AddToHomeScreenIcon fontSize="small" sx={{ ml: 0.5 }} />
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleDismiss}
              fullWidth
              sx={{ mt: 1 }}
            >
              {t('buttons.gotIt', 'Got it!')}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" mb={2} color="text.secondary">
              {t('installPrompt.android.description')}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleInstall}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{ mt: 1 }}
            >
              {t('installPrompt.android.installButton')}
            </Button>
          </Box>
        )}
      </Paper>
    </Fade>
  );
};

export default InstallPrompt;
